import React from 'react';
import { Box, Typography, IconButton, Menu, MenuItem, ListItemIcon, ListItemText, Tooltip } from '@mui/material';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import InventoryIcon from '@mui/icons-material/Inventory';
import WarningIcon from '@mui/icons-material/Warning';
import CampaignIcon from '@mui/icons-material/Campaign';
import InfoIcon from '@mui/icons-material/Info';
import { StatusChip } from '../ui';
import { ADVERTISEMENT_TEMPLATES, ADVERTISEMENT_STATUSES } from '../../constants/advertisementTemplates';

const TEMPLATE_ICONS = {
  discount_sale: LocalOfferIcon,
  restock: InventoryIcon,
  low_stock: WarningIcon,
  announcement: CampaignIcon,
  service_notice: InfoIcon,
};

const buildAdvertisementColumns = ({ t, openRowMenu, handleToggleActive }) => [
  {
    field: 'status',
    headerName: t('Status'),
    width: 120,
    renderCell: (params) => {
      const statusInfo = ADVERTISEMENT_STATUSES[params.value] || ADVERTISEMENT_STATUSES.draft;
      return <StatusChip status={params.value} label={t(statusInfo.label)} />;
    },
  },
  {
    field: 'title',
    headerName: t('Title'),
    flex: 1.4,
    minWidth: 180,
    renderCell: (params) => <Typography variant="subtitle2" noWrap>{params.value}</Typography>,
  },
  {
    field: 'templateType',
    headerName: t('Template'),
    flex: 1,
    minWidth: 150,
    hideOnTablet: true,
    renderCell: (params) => {
      const template = ADVERTISEMENT_TEMPLATES[params.value] || ADVERTISEMENT_TEMPLATES.announcement;
      const Icon = TEMPLATE_ICONS[params.value] || CampaignIcon;
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Icon fontSize="small" color="action" />
          <Typography variant="body2" noWrap>{t(template.name)}</Typography>
        </Box>
      );
    },
  },
  {
    field: 'schedule',
    headerName: t('Schedule'),
    flex: 1.2,
    minWidth: 170,
    sortable: false,
    renderCell: (params) => {
      const row = params.row;
      const startDate = row.startsAt ? new Date(row.startsAt).toLocaleDateString() : t('Immediately');
      const endDate = row.endsAt ? new Date(row.endsAt).toLocaleDateString() : t('Never Expires');
      return (
        <Box>
          <Typography variant="caption" display="block">{t('Starts')}: {startDate}</Typography>
          <Typography variant="caption" display="block">{t('Ends')}: {endDate}</Typography>
        </Box>
      );
    },
  },
  {
    field: 'priority',
    headerName: t('Priority'),
    width: 90,
    type: 'number',
    hideOnTablet: true,
  },
  {
    field: 'actions',
    headerName: '',
    width: 110,
    sortable: false,
    filterable: false,
    renderCell: (params) => {
      const ad = params.row;
      const isActive = ad.status === 'active';
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Tooltip title={isActive ? t('Deactivate') : t('Activate')}>
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); handleToggleActive(ad); }}
              aria-label={isActive ? t('Deactivate') : t('Activate')}
            >
              {isActive ? <PauseIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); openRowMenu(e, ad); }} aria-label={t('Actions')}>
            <MoreHorizIcon fontSize="small" />
          </IconButton>
        </Box>
      );
    },
  },
];

export const AdvertisementRowMenu = ({ t, anchorEl, ad, onClose, onDuplicate, onDelete }) => (
  <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={onClose}>
    <MenuItem onClick={() => { onDuplicate(ad); onClose(); }}>
      <ListItemIcon><ContentCopyIcon fontSize="small" /></ListItemIcon>
      <ListItemText>{t('Duplicate Advertisement')}</ListItemText>
    </MenuItem>
    <MenuItem onClick={() => { onDelete(ad); onClose(); }}>
      <ListItemIcon><DeleteIcon fontSize="small" /></ListItemIcon>
      <ListItemText>{t('Delete Advertisement')}</ListItemText>
    </MenuItem>
  </Menu>
);

export default buildAdvertisementColumns;
